/**
 * Channel framework (ENGINE-SPEC §3.2, §3.6–§3.10): the rate/proportion estimator, priors,
 * overdispersion, shrinkage, the display ramp (D1/D2 fixes live in each channel's ramp data),
 * abstention and evidence storage. Channels supply a per-paragraph `Measurement`; everything
 * else is derived here so all channels report the same fields (ED-3).
 */
import { clamp, clopperPearson, garwood, ramp, rampInverse } from '../util/stats.ts';
import type {
	ChannelDef,
	ChannelImpl,
	ChannelResult,
	DocEstimate,
	EvidenceBuilder,
	EvidenceSpans,
	Measurement,
	Prior,
	Ramp,
	RunContext,
} from './types.ts';

/* ---------------- evidence ---------------- */

export function makeEvidence(): EvidenceBuilder {
	let cap = 64;
	let n = 0;
	let start = new Uint32Array(cap);
	let end = new Uint32Array(cap);
	let feature = new Uint8Array(cap);
	let weight = new Float32Array(cap);
	const grow = () => {
		cap *= 2;
		const s = new Uint32Array(cap);
		s.set(start);
		start = s;
		const e = new Uint32Array(cap);
		e.set(end);
		end = e;
		const f = new Uint8Array(cap);
		f.set(feature);
		feature = f;
		const w = new Float32Array(cap);
		w.set(weight);
		weight = w;
	};
	return {
		add(s, e, f, w) {
			if (n === cap) grow();
			start[n] = s;
			end[n] = e;
			feature[n] = f;
			weight[n] = w;
			n++;
		},
		build(): EvidenceSpans {
			return {
				start: start.slice(0, n),
				end: end.slice(0, n),
				feature: feature.slice(0, n),
				weight: weight.slice(0, n),
			};
		},
		get length() {
			return n;
		},
	};
}

export const emptyEvidence = (): EvidenceSpans => ({
	start: new Uint32Array(0),
	end: new Uint32Array(0),
	feature: new Uint8Array(0),
	weight: new Float32Array(0),
});

/* ---------------- priors (§3.7) ---------------- */

const M_RANGE: Record<ChannelDef['exposureUnit'], [number, number]> = {
	words: [500, 5000],
	sentences: [30, 300],
	paragraphs: [15, 150],
	units: [4, 40],
	marks: [50, 500],
	lines: [10, 100],
};

/**
 * Derive a prior from the display ramp: μ = x where the ramp gives 20 (or the x of the ramp's
 * minimum-y point when it never reaches 20); m = 3/μ (exposure units) clamped; φ0 by family.
 */
export function derivePrior(
	rampPts: Ramp,
	unitScale: number,
	exposureUnit: ChannelDef['exposureUnit'],
	phi0: number
): Prior {
	let mu = rampInverse(rampPts, 20);
	if (mu === null) {
		let best = rampPts[0] as readonly [number, number];
		for (const p of rampPts) if (p[1] < best[1]) best = p;
		mu = best[0];
	}
	const muRate = mu / unitScale;
	const [lo, hi] = M_RANGE[exposureUnit];
	const m = muRate > 0 ? clamp(3 / muRate, lo, hi) : hi;
	return { mu, m, phi0 };
}

/* ---------------- estimators ---------------- */

/** Quasi-Poisson overdispersion from paragraph Pearson residuals (§3.2). */
export function overdispersion(
	k: Float64Array,
	e: Float64Array,
	K: number,
	E: number,
	phi0: number,
	binomial = false
): number {
	if (E <= 0) return phi0;
	const lam = K / E;
	const chi: number[] = [];
	for (let p = 0; p < k.length; p++) {
		const ep = e[p] as number;
		if (ep <= 0) continue;
		const mu = lam * ep;
		const v = binomial ? Math.max(mu * (1 - lam), 1e-9) : Math.max(mu, 1e-9);
		const r = ((k[p] as number) - mu) ** 2 / v;
		chi.push(r);
	}
	if (chi.length < 10 || K < 5) return phi0;
	chi.sort((a, b) => a - b);
	const keep = Math.floor(chi.length * 0.9);
	let s = 0;
	for (let i = 0; i < keep; i++) s += chi[i] as number;
	return clamp(s / Math.max(keep - 1, 1), 1, 10);
}

export interface RateFit {
	raw: number;
	estimate: number;
	ci90: [number, number];
	deviation: number;
	kappa: number;
	score: number;
	scoreCi90: [number, number];
}

/** Shrunken rate estimate, interval, deviation, κ and score for weighted events K over exposure E. */
export function fitRate(
	def: ChannelDef,
	K: number,
	E: number,
	phi: number,
	binomial = false
): RateFit {
	const { mu, m } = def.prior;
	const muRate = mu / def.unitScale;
	const raw = E > 0 ? K / E : 0;
	// `shrink: false` (residue channels, C-OB01-NOSHRINK): the unshrunk rate; κ below is still the data weight
	const shrink =
		def.shrink === false
			? (Kx: number) => (E > 0 ? (Kx / E) * def.unitScale : 0)
			: (Kx: number) => ((Kx / phi + m * muRate) / (E / phi + m)) * def.unitScale;
	const estimate = shrink(K);
	const kappa = E > 0 ? E / phi / (E / phi + m) : 0;
	// interval on K, widened by √φ, then mapped through the same shrinkage (or the raw rate when unshrunk)
	let klo: number;
	let khi: number;
	if (binomial) {
		const [pl, ph] = clopperPearson(K, E, 0.9);
		klo = pl * E;
		khi = ph * E;
	} else {
		[klo, khi] = garwood(K, 0.9);
	}
	const w = Math.sqrt(phi);
	klo = Math.max(0, K - (K - klo) * w);
	khi = K + (khi - K) * w;
	const ci90: [number, number] = [shrink(klo), shrink(khi)];
	const se = Math.sqrt((phi * muRate) / Math.max(E, 1e-9));
	const deviation = se > 0 ? (def.direction * (raw - muRate)) / se : 0;
	const score = ramp(estimate, def.ramp);
	const s1 = ramp(ci90[0], def.ramp);
	const s2 = ramp(ci90[1], def.ramp);
	return {
		raw,
		estimate,
		ci90,
		deviation,
		kappa,
		score,
		scoreCi90: [Math.min(s1, s2), Math.max(s1, s2)],
	};
}

/* ---------------- results ---------------- */

const auxByResult = new WeakMap<ChannelResult, NonNullable<Measurement['aux']>>();

/** The measurement side data a rate channel produced for `result` (BENCH-V2-SPEC §5.1), if any. */
export const auxOf = (result: ChannelResult): Measurement['aux'] | undefined =>
	auxByResult.get(result);

const ZERO_EST: DocEstimate = {
	K: 0,
	E: 0,
	raw: 0,
	estimate: 0,
	ci90: [0, 0],
	deviation: 0,
	phi: 1,
	kappa: 0,
	score: 0,
	scoreCi90: [0, 0],
};

/** Numeric fields of an abstaining result are zero; consumers must test `status` (or use `scoreOf`). */
export function abstainResult(
	def: ChannelDef,
	reason: string,
	notes: string[] = []
): ChannelResult {
	return {
		id: def.id,
		status: 'abstain',
		reason,
		notes,
		doc: { ...ZERO_EST, ci90: [0, 0], scoreCi90: [0, 0] },
		segments: [],
		spikes: [],
		units: [],
		evidence: emptyEvidence(),
		features: {},
		summary: [],
	};
}

/** T-PROP-01 view: the score, or null when the channel abstained. */
export const scoreOf = (r: ChannelResult): number | null =>
	r.status === 'scored' ? r.doc.score : null;

/** Language-pack gate (§2.12): native channels abstain on unsupported languages. */
export function languageGate(
	def: ChannelDef,
	ctx: RunContext
): { abstain?: string; note?: string } {
	const lang = ctx.doc.language.code;
	if (def.lang === 'native' && ctx.doc.flags.languageUnsupported)
		return { abstain: `not available for ${lang}` };
	if (def.lang === 'agnostic' && ctx.doc.flags.languageUnsupported)
		return { note: `uncalibrated for ${lang}` };
	return {};
}

export interface ProseExposureInfo {
	K: number;
	E: number;
}

export function sumArrays(k: Float64Array, e: Float64Array): ProseExposureInfo {
	let K = 0;
	let E = 0;
	for (let i = 0; i < k.length; i++) {
		K += k[i] as number;
		E += e[i] as number;
	}
	return { K, E };
}

/**
 * Build a rate/proportion channel from its measurement function (§3.2). The measurement is the
 * only channel-specific code; the doc-level fit, abstention and result assembly are shared.
 */
export function rateChannel(
	def: ChannelDef,
	measure: (ctx: RunContext) => Measurement
): ChannelImpl {
	return {
		def,
		phase: 'event',
		measure,
		run(ctx: RunContext): ChannelResult {
			const gate = languageGate(def, ctx);
			if (gate.abstain) return abstainResult(def, gate.abstain);
			let m: Measurement;
			try {
				m = measure(ctx);
			} catch (err) {
				return abstainResult(
					def,
					`internal-error: ${err instanceof Error ? err.message : String(err)}`
				);
			}
			const notes = [...(m.notes ?? [])];
			if (gate.note) notes.push(gate.note);
			if (m.abstain) return abstainResult(def, m.abstain, notes);
			const { K, E } = sumArrays(m.k, m.e);
			if (E < def.minExposure) {
				return abstainResult(
					def,
					`insufficient text: ${fmtNum(E)} ${def.exposureUnit} < ${def.minExposure}`,
					notes
				);
			}
			const binomial = def.estimator === 'proportion';
			const phi = overdispersion(m.k, m.e, K, E, def.prior.phi0, binomial);
			const fit = fitRate(def, K, E, phi, binomial);
			const estimate = m.displayOverride ?? fit.estimate;
			const score = m.displayOverride !== undefined ? ramp(estimate, def.ramp) : fit.score;
			const result: ChannelResult = {
				id: def.id,
				status: 'scored',
				notes,
				doc: {
					K,
					E,
					raw: fit.raw,
					estimate,
					ci90: fit.ci90,
					deviation: fit.deviation,
					phi,
					kappa: fit.kappa,
					score,
					scoreCi90: m.displayOverride !== undefined ? [score, score] : fit.scoreCi90,
				},
				perParagraph: { k: m.k, e: m.e },
				segments: [],
				spikes: [],
				units: [],
				evidence: m.evidence ? m.evidence.build() : emptyEvidence(),
				features: m.features ?? {},
				summary: m.summary ?? [],
			};
			if (m.aux) auxByResult.set(result, m.aux);
			return result;
		},
	};
}

const fmtNum = (x: number) => (Math.abs(x) >= 100 ? x.toFixed(0) : x.toFixed(1));

/**
 * Assemble a scored result for channels that compute their own estimate (recurrence,
 * distributional, structural): pass the estimate in ramp units, an effective K/E for κ, etc.
 */
export function scoredResult(
	def: ChannelDef,
	f: {
		K: number;
		E: number;
		raw: number;
		estimate: number;
		ci90?: [number, number];
		deviation?: number;
		phi?: number;
		kappa: number;
		/** Explicit display score for composite channels (e.g. the mean of two sub-ramps); overrides ramp(estimate). */
		score?: number;
		scoreCi90?: [number, number];
		perParagraph?: { k: Float64Array; e: Float64Array };
		evidence?: EvidenceSpans;
		notes?: string[];
		features?: Record<string, number>;
		summary?: string[];
	}
): ChannelResult {
	const ci: [number, number] = f.ci90 ?? [f.estimate, f.estimate];
	const s = f.score ?? ramp(f.estimate, def.ramp);
	const s1 = f.scoreCi90 ? f.scoreCi90[0] : ramp(ci[0], def.ramp);
	const s2 = f.scoreCi90 ? f.scoreCi90[1] : ramp(ci[1], def.ramp);
	return {
		id: def.id,
		status: 'scored',
		notes: f.notes ?? [],
		doc: {
			K: f.K,
			E: f.E,
			raw: f.raw,
			estimate: f.estimate,
			ci90: ci,
			deviation: f.deviation ?? 0,
			phi: f.phi ?? 1,
			kappa: clamp(f.kappa, 0, 1),
			score: s,
			scoreCi90: [Math.min(s1, s2), Math.max(s1, s2)],
		},
		perParagraph: f.perParagraph,
		segments: [],
		spikes: [],
		units: [],
		evidence: f.evidence ?? emptyEvidence(),
		features: f.features ?? {},
		summary: f.summary ?? [],
	};
}
